/**
 * LookToBookPieChart Component
 *
 * Pie/donut chart showing per-appraiser Look to Book distribution for the
 * current location. Sized by # Appraisals; tooltip shows the three vAuto
 * stats. Sits inside the Sales Manager Performance card right column, below
 * the existing Gross Distribution chart.
 *
 * Data source: vAuto Appraisal Summary Report — past 60 days
 * https://profittime.vauto.app.coxautoinc.com/Va/Dashboard/Provision.aspx?tab=EnterpriseInventory
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export interface LookToBookAppraiser {
  name: string;
  appraisals: number;
  takenIn: number;
  lookToBook: number;
}

interface LookToBookPieChartProps {
  appraisers: LookToBookAppraiser[];
  totalAppraisals: number;
  totalTakenIn: number;
  totalLookToBook: number;
}

// Blue palette to match the NEW_VEHICLE department theme used in the
// neighboring Gross Distribution chart.
const COLORS = [
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#f97316', // orange-500
  '#fb923c', // orange-400
  '#fdba74', // orange-300
  '#10b981', // emerald-500
  '#34d399', // emerald-400
  '#6ee7b7', // emerald-300
  '#a78bfa', // violet-400
  '#c4b5fd', // violet-300
];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{d.name}</p>
        <p className="text-sm text-gray-600">
          <span className="font-medium text-blue-600">{d.appraisals}</span> Appraisals
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium text-blue-600">{d.takenIn}</span> Taken In
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">{d.lookToBook.toFixed(1)}%</span> Look to Book
        </p>
      </div>
    );
  }
  return null;
};

const renderLegendText = (value: string) => {
  const maxLength = 15;
  return value.length > maxLength ? value.substring(0, maxLength) + '…' : value;
};

export const LookToBookPieChart: React.FC<LookToBookPieChartProps> = ({
  appraisers,
  totalAppraisals,
  totalTakenIn,
  totalLookToBook,
}) => {
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (appraisers.length === 0 || totalAppraisals === 0) return [];

    // Sort by # Appraisals descending. Largest book of business gets the
    // boldest color so the visual hierarchy matches the data weight.
    const sorted = [...appraisers].sort((a, b) => b.appraisals - a.appraisals);

    if (sorted.length <= MAX_SLICES) {
      return sorted.map((a, i) => ({
        name: a.name,
        value: a.appraisals,
        appraisals: a.appraisals,
        takenIn: a.takenIn,
        lookToBook: a.lookToBook,
        color: COLORS[i % COLORS.length],
      }));
    }

    const top = sorted.slice(0, MAX_SLICES - 1);
    const tail = sorted.slice(MAX_SLICES - 1);
    const tailAppraisals = tail.reduce((s, a) => s + a.appraisals, 0);
    const tailTakenIn = tail.reduce((s, a) => s + a.takenIn, 0);
    const tailLookToBook = tailAppraisals === 0 ? 0 : (tailTakenIn / tailAppraisals) * 100;

    const result = top.map((a, i) => ({
      name: a.name,
      value: a.appraisals,
      appraisals: a.appraisals,
      takenIn: a.takenIn,
      lookToBook: a.lookToBook,
      color: COLORS[i % COLORS.length],
    }));

    if (tailAppraisals > 0) {
      result.push({
        name: `Others (${tail.length})`,
        value: tailAppraisals,
        appraisals: tailAppraisals,
        takenIn: tailTakenIn,
        lookToBook: tailLookToBook,
        color: '#9ca3af',
      });
    }
    return result;
  }, [appraisers, totalAppraisals]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No appraisal data
      </div>
    );
  }

  return (
    <div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={75}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              formatter={renderLegendText}
              wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-600 text-center mt-1">
        <span className="font-medium">{totalAppraisals}</span> Appraisals ·{' '}
        <span className="font-medium">{totalTakenIn}</span> Taken In ·{' '}
        <span className="font-medium">{totalLookToBook.toFixed(1)}%</span> Look to Book
      </p>
    </div>
  );
};
